import clsx from "clsx";
import { useEffect, useRef, type ReactNode } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronLeft,
  faChevronRight,
  faCircleCheck,
} from "@awesome.me/kit-70c1f0055f/icons/classic/regular";
import { CC_PANEL } from "@/components/case-checking/caseCheckingChrome";
import { ReviewInProgressDisclaimer } from "@/components/case-checking/ReviewInProgressDisclaimer";
import { ICON_CHECK_PASSED } from "@/components/case-checking/caseCheckingStatusBadges";
import type { ReviewDocumentItem } from "@/components/case-checking/ReviewDocumentsRail";
import { Button } from "@/components/ui/button/Button";

export type ReviewWorkflowStep =
  | "narrative"
  | "documents"
  | "checklist"
  | "rules"
  | "assessment"
  | "actions"
  | "summary";

const STEPS: { id: ReviewWorkflowStep; label: string }[] = [
  { id: "narrative", label: "Case narrative" },
  { id: "documents", label: "Documents" },
  { id: "checklist", label: "Completeness" },
  { id: "rules", label: "Rules" },
  { id: "assessment", label: "Assessment" },
  { id: "actions", label: "Actions" },
  { id: "summary", label: "Case summary" },
];

export const REVIEW_WORKFLOW_STEP_ORDER = STEPS.map((s) => s.id);

export function reviewWorkflowStepLabel(step: ReviewWorkflowStep): string {
  return STEPS.find((s) => s.id === step)?.label ?? step;
}

export function isAdvancingWorkflowStep(from: ReviewWorkflowStep, to: ReviewWorkflowStep): boolean {
  return REVIEW_WORKFLOW_STEP_ORDER.indexOf(to) > REVIEW_WORKFLOW_STEP_ORDER.indexOf(from);
}

function nextStepLabel(step: ReviewWorkflowStep, atEnd: boolean): string {
  if (atEnd) return "Done";
  if (step === "actions") return "Finish";
  if (step === "documents") return "Continue";
  if (step === "narrative") return "Mark as read";
  return "Next step";
}

function WorkflowStepNav({
  placement,
  step,
  atStart,
  atEnd,
  canAdvance,
  onPrev,
  onNext,
  nextLabel,
}: {
  placement: "top" | "bottom";
  step: ReviewWorkflowStep;
  atStart: boolean;
  atEnd: boolean;
  canAdvance: boolean;
  onPrev: () => void;
  onNext: () => void;
  nextLabel: string;
}) {
  const idSuffix = placement === "top" ? "-top" : "-bottom";
  return (
    <div
      className={clsx(
        "flex items-center justify-between gap-3",
        placement === "top" ? "pb-2 pt-0.5" : "pt-3",
      )}
      data-testid={`review-workflow-nav${idSuffix}`}
    >
      <Button
        plain
        onClick={onPrev}
        disabled={atStart}
        data-testid={`review-workflow-prev${idSuffix}`}
      >
        <FontAwesomeIcon icon={faChevronLeft} className="mr-1 h-3 w-3" />
        Previous
      </Button>
      <Button
        color="indigo"
        onClick={onNext}
        disabled={atEnd || !canAdvance}
        data-testid={`review-workflow-next${idSuffix}`}
      >
        {nextLabel}
        {!atEnd ? <FontAwesomeIcon icon={faChevronRight} className="ml-1 h-3 w-3" /> : null}
      </Button>
    </div>
  );
}

function WorkflowStepper({
  step,
  onStepChange,
  completedSteps,
}: {
  step: ReviewWorkflowStep;
  onStepChange: (next: ReviewWorkflowStep) => void;
  completedSteps: Set<ReviewWorkflowStep>;
}) {
  const activeIndex = STEPS.findIndex((s) => s.id === step);

  return (
    <nav
      className={clsx(CC_PANEL, "py-3 overflow-x-auto")}
      aria-label="Review progress"
      data-testid="review-workflow-stepper"
    >
      <ol className="flex min-w-max items-center gap-1 sm:gap-0">
        {STEPS.map((s, index) => {
          const active = s.id === step;
          const done = completedSteps.has(s.id);
          const reachable = index <= activeIndex || done;
          const stepState = active ? "current step" : done ? "completed" : "not started";
          const stepAriaLabel = `${s.label}, step ${index + 1} of ${STEPS.length}, ${stepState}`;
          return (
            <li key={s.id} className="flex items-center">
              <Button
                plain
                type="button"
                disabled={!reachable}
                onClick={() => onStepChange(s.id)}
                aria-label={stepAriaLabel}
                aria-current={active ? "step" : undefined}
                className={clsx(
                  "flex items-center gap-2 rounded-lg px-2 py-1.5 text-left transition-colors sm:px-3",
                  active ? "bg-slate-100 dark:bg-white/[0.06] ring-1 ring-slate-200 dark:ring-white/10" : "hover:bg-slate-50 dark:hover:bg-white/[0.03]",
                  !reachable && "opacity-50 pointer-events-none",
                )}
                data-testid={`review-workflow-step-${s.id}`}
              >
                {done ? (
                  <FontAwesomeIcon icon={faCircleCheck} className={clsx("h-6 w-6 shrink-0", ICON_CHECK_PASSED)} />
                ) : (
                  <span
                    className={clsx(
                      "flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[11px] font-semibold ring-1 ring-inset",
                      active
                        ? "bg-indigo-500/15 text-indigo-800 dark:text-indigo-200 ring-indigo-500/30"
                        : "bg-slate-200 text-slate-500 ring-slate-300 dark:bg-slate-800 dark:text-slate-400 dark:ring-white/10",
                    )}
                  >
                    {index + 1}
                  </span>
                )}
                <span className="min-w-0 hidden truncate text-xs font-medium text-slate-700 dark:text-slate-200 sm:block">
                  {s.label}
                </span>
              </Button>
              {index < STEPS.length - 1 ? (
                <span className="mx-1 hidden h-px w-4 bg-slate-100 dark:bg-white/10 sm:block sm:w-6" aria-hidden />
              ) : null}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}

export function CaseReviewWorkflow({
  step,
  onStepChange,
  completedSteps,
  caseId: _caseId,
  documents: _documents,
  documentsLoading: _documentsLoading,
  narrativeStep,
  documentsStep,
  checklistStep,
  rulesStep,
  assessmentStep,
  actionsStep,
  summaryStep,
  canAdvance = true,
  onBeforeAdvance,
  advanceLabel,
  caseCheckInProgress = false,
}: {
  step: ReviewWorkflowStep;
  onStepChange: (next: ReviewWorkflowStep) => void;
  completedSteps: Set<ReviewWorkflowStep>;
  caseId: string;
  documents: ReviewDocumentItem[];
  documentsLoading?: boolean;
  narrativeStep: ReactNode;
  documentsStep: ReactNode;
  checklistStep: ReactNode;
  rulesStep: ReactNode;
  assessmentStep: ReactNode;
  actionsStep: ReactNode;
  summaryStep: ReactNode;
  /** When false, the advance button is disabled (e.g. incomplete verdict without a note). */
  canAdvance?: boolean;
  /** Return false to cancel advancing to the next step. */
  onBeforeAdvance?: () => boolean;
  /** Overrides the default next-button label for the current step. */
  advanceLabel?: string;
  /** When true, shows a banner that agent outputs may change while the mission runs. */
  caseCheckInProgress?: boolean;
}) {
  const stepIndex = STEPS.findIndex((s) => s.id === step);
  const atStart = stepIndex <= 0;
  const atEnd = stepIndex >= STEPS.length - 1;
  const prevStepRef = useRef(step);

  useEffect(() => {
    if (prevStepRef.current === step) return;
    prevStepRef.current = step;
    // Reset scroll within the OS window content pane only — avoid document scrollIntoView.
    const pane = document.querySelector('[data-testid="window-content-scroll"]') as HTMLElement | null;
    if (pane) pane.scrollTop = 0;
  }, [step]);

  const body =
    step === "narrative"
      ? narrativeStep
      : step === "documents"
        ? documentsStep
        : step === "checklist"
          ? checklistStep
          : step === "rules"
            ? rulesStep
            : step === "assessment"
              ? assessmentStep
              : step === "actions"
                ? actionsStep
                : summaryStep;

  const tryChangeStep = (next: ReviewWorkflowStep) => {
    if (isAdvancingWorkflowStep(step, next)) {
      if (!canAdvance) return;
      if (onBeforeAdvance && !onBeforeAdvance()) return;
    }
    onStepChange(next);
  };

  const goPrev = () => {
    if (!atStart) tryChangeStep(STEPS[stepIndex - 1].id);
  };
  const goNext = () => {
    if (atEnd) return;
    tryChangeStep(STEPS[stepIndex + 1].id);
  };

  const activeStepMeta = STEPS.find((s) => s.id === step);

  const nextLabel = advanceLabel ?? nextStepLabel(step, atEnd);

  const navProps = {
    step,
    atStart,
    atEnd,
    canAdvance,
    onPrev: goPrev,
    onNext: goNext,
    nextLabel,
  };

  return (
    <div className="space-y-4" data-testid="case-review-workflow">
      <WorkflowStepper step={step} onStepChange={tryChangeStep} completedSteps={completedSteps} />
      <WorkflowStepNav placement="top" {...navProps} />

      <div className="min-w-0 space-y-4">
        {activeStepMeta ? (
          <h2 className="sr-only">{activeStepMeta.label}</h2>
        ) : null}
        {caseCheckInProgress ? <ReviewInProgressDisclaimer /> : null}
        {body}

        <WorkflowStepNav placement="bottom" {...navProps} />
      </div>
    </div>
  );
}
